import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { graphqlQuery, graphqlMutation } from '../utils/graphqlApi';
import { getRunbook } from '../graphql/queries';
import { refreshImageUrls as refreshImageUrlsMutation } from '../graphql/mutations';

const parseScenarioHeader = (description = '') => {
  if (typeof description !== 'string') return null;
  const normalized = description
    .replace(/^\s*[-*]\s*/, '')
    .replace(/^\s*\*\*/, '')
    .replace(/^[\s"'“”‘’`]+/, '')
    .trim();

  const lines = normalized
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  const primaryLine = lines[0] || normalized;
  let match = primaryLine.match(/^scenario\s*(\d+)\s*(?:[:\-–—]\s*)?(.*)$/i);
  if (!match) {
    match = primaryLine.match(/\bscenario\s*(\d+)\b\s*(?:[:\-–—]\s*)?(.*)$/i);
  }
  if (!match) return null;

  const rawTitle = (match[2] || '')
    .replace(/^[\s:;\-–—]+/, '')
    .replace(/\*+$/, '')
    .trim();
  return {
    number: match[1],
    title: rawTitle,
  };
};

function ViewRunbook() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [runbook, setRunbook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [imageUrls, setImageUrls] = useState({});
  const [selectedScenarioKey, setSelectedScenarioKey] = useState('');
  const [showAllScenarios, setShowAllScenarios] = useState(false);

  const refreshStepImageUrls = useCallback(async (steps) => {
    if (!steps || steps.length === 0) return;
    try {
      const stepsWithKeys = steps
        .map((step, index) => ({
          step_number: step.step_number || String(index + 1),
          description: step.description || '',
          image_s3_key: step.image_s3_key || '',
        }))
        .filter((s) => s.image_s3_key);

      if (stepsWithKeys.length === 0) return;

      const response = await graphqlMutation(refreshImageUrlsMutation, {
        input: {
          body: JSON.stringify({ runbook_steps: stepsWithKeys }),
        },
      });

      const lambdaResponse = response.refreshImageUrls;
      let data =
        typeof lambdaResponse.body === 'string'
          ? JSON.parse(lambdaResponse.body)
          : lambdaResponse.body;

      if (typeof data === 'string') {
        data = JSON.parse(data);
      }

      const urls = {};
      if (data.runbook_steps) {
        data.runbook_steps.forEach((rs) => {
          if (rs.image_url || rs.imageUrl) {
            urls[rs.step_number] = rs.image_url || rs.imageUrl;
          }
        });
      }
      setImageUrls(urls);
    } catch (err) {
      console.warn('[ViewRunbook] Error refreshing image URLs:', err.message);
    }
  }, []);

  const getStepImageUrl = (step) => step.image_url || step.imageUrl || step.URL || step.url || '';
  const getStepImageKey = (step) => step.image_s3_key || step.imageS3Key || step.s3_key || step.s3Key || '';
  const getStepNumber = (step, index) => step.step_number || step['Step Number'] || String(index + 1);
  const getStepDescription = (step) => step.description || step.step_description || step.Description || '';

  const refreshRunbookImageUrls = useCallback(async (steps) => {
    if (!steps || steps.length === 0) return steps;
    try {
      const stepsWithExistingImageUrls = steps.some((step) => getStepImageUrl(step));
      const runbookSteps = steps
        .map((step, index) => ({
          step_number: getStepNumber(step, index),
          description: getStepDescription(step),
          image_s3_key: getStepImageKey(step),
        }))
        .filter((s) => s.image_s3_key);

      if (runbookSteps.length === 0) {
        if (!stepsWithExistingImageUrls) {
          console.info('[ViewRunbook][RefreshImageUrls] No image references found in steps. Skipping refresh.');
        }
        return steps;
      }

      const response = await graphqlMutation(refreshImageUrlsMutation, {
        input: {
          body: JSON.stringify({ runbook_steps: runbookSteps }),
        },
      });

      console.log('[ViewRunbook][RefreshImageUrls] Response:', response);

      const lambdaResponse = response.refreshImageUrls;
      let data =
        typeof lambdaResponse.body === 'string'
          ? JSON.parse(lambdaResponse.body)
          : lambdaResponse.body;
      if (typeof data === 'string') {
        data = JSON.parse(data);
      }

      console.log('[ViewRunbook][RefreshImageUrls] Parsed data:', data);

      const refreshedSteps = steps.map((step, index) => {
        const refreshedStep = data.runbook_steps?.find(
          (rs) => rs.step_number === getStepNumber(step, index)
        );
        if (refreshedStep && (refreshedStep.image_url || refreshedStep.imageUrl)) {
          return { ...step, image_url: refreshedStep.image_url || refreshedStep.imageUrl };
        }
        return step;
      });

      return refreshedSteps;
    } catch (err) {
      console.warn('[ViewRunbook][RefreshImageUrls] Error refreshing URLs:', err.message);
      return steps;
    }
  }, []);

  useEffect(() => {
    const fetchRunbookData = async () => {
      try {
        setLoading(true);
        const keyword = decodeURIComponent(id);
        const result = await graphqlQuery(getRunbook, { keyword });
        let data = result.getRunbook;
        console.log('[ViewRunbook] Fetched runbook data:', data);
        
        if (data.runbook_steps && data.runbook_steps.length > 0) {
          console.log('[ViewRunbook] Found ' + data.runbook_steps.length + ' steps');
          const refreshedSteps = await refreshRunbookImageUrls(data.runbook_steps);
          data = { ...data, runbook_steps: refreshedSteps };
        }
        
        setRunbook(data);
        
        if (data.runbook_steps && data.runbook_steps.length > 0) {
          await refreshStepImageUrls(data.runbook_steps);
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching runbook:', error);
        setError(error.message || 'Failed to load runbook');
        setLoading(false);
      }
    };

    fetchRunbookData();
  }, [id, refreshRunbookImageUrls, refreshStepImageUrls]);

  const steps = useMemo(() => runbook?.runbook_steps || [], [runbook]);
  const scenarioSections = useMemo(() => {
    const sections = [];
    let currentSection = {
      key: 'general',
      label: 'General',
      scenarioNumber: null,
      steps: [],
    };

    steps.forEach((step, index) => {
      const description = step?.description || '';
      const parsedScenario = parseScenarioHeader(description);

      if (parsedScenario) {
        if (currentSection.steps.length > 0) {
          sections.push(currentSection);
        }

        currentSection = {
          key: `scenario-${parsedScenario.number}-${parsedScenario.title || index}`,
          label: `Scenario ${parsedScenario.number}${parsedScenario.title ? `: ${parsedScenario.title}` : ''}`,
          scenarioNumber: parsedScenario.number,
          steps: [{ step, index }],
        };
      } else {
        currentSection.steps.push({ step, index });
      }
    });

    if (currentSection.steps.length > 0) {
      sections.push(currentSection);
    }

    return sections;
  }, [steps]);

  const scenarioOnlySections = useMemo(
    () => scenarioSections.filter((s) => s.scenarioNumber),
    [scenarioSections]
  );
  const scenarioCount = scenarioOnlySections.length;
  const hasScenarioLayout = scenarioCount > 0;

  useEffect(() => {
    if (!scenarioSections.length) {
      setSelectedScenarioKey('');
      return;
    }

    if (!selectedScenarioKey || !scenarioSections.some((s) => s.key === selectedScenarioKey)) {
      const defaultSection = scenarioOnlySections[0] || scenarioSections[0];
      setSelectedScenarioKey(defaultSection.key);
    }
  }, [scenarioSections, scenarioOnlySections, selectedScenarioKey]);

  const visibleSections = showAllScenarios
    ? scenarioSections
    : scenarioSections.filter((s) => s.key === selectedScenarioKey);

  if (loading) {
    return (
      <div className="page-container">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading runbook details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">Error</h4>
          <p>{error}</p>
          <button className="kone-btn kone-btn-primary" onClick={() => navigate('/runbook_list')}>
            Back to Runbook List
          </button>
        </div>
      </div>
    );
  }

  if (!runbook) {
    return (
      <div className="page-container">
        <div className="alert alert-warning" role="alert">
          <p>Runbook not found</p>
          <button className="kone-btn kone-btn-primary" onClick={() => navigate('/runbook_list')}>
            Back to Runbook List
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="kone-section-title">View Runbook</h2>
        <button 
          className="kone-btn kone-btn-secondary"
          onClick={() => navigate('/runbook_list')}
        >
          Back to Runbook List
        </button>
      </div>

      {/* Basic Information */}
      <div className="card mb-4">
        <div className="card-header">
          <h5 className="mb-0">Basic Information</h5>
        </div>
        <div className="card-body">
          <div className="row mb-3">
            <div className="col-md-6">
              <label className="kone-label">Keyword</label>
              <input
                type="text"
                className="kone-input"
                value={runbook.keyword || ''}
                readOnly
              />
            </div>
            <div className="col-md-6">
              <label className="kone-label">Tribe</label>
              <input
                type="text"
                className="kone-input"
                readOnly
              />
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-md-6">
              <label className="kone-label">Is Automation</label>
              <input
                type="text"
                className="kone-input"
                value={runbook.is_automation ? 'Yes' : 'No'}
                readOnly
              />
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-md-6">
              <label className="kone-label">Modified By</label>
              <input
                type="text"
                className="kone-input"
                value={runbook.modified_by || '-'}
                readOnly
              />
            </div>
            <div className="col-md-6">
              <label className="kone-label">Modified At</label>
              <input
                type="text"
                className="kone-input"
                value={runbook.modified_at ? new Date(runbook.modified_at).toLocaleString() : '-'}
                readOnly
              />
            </div>
          </div>
        </div>
      </div>

      {/* Steps */}
      {steps.length > 0 && (
        <div className="card mb-4">
          <div className="card-header">
            <h5 className="mb-0">
              Resolution Steps ({steps.length})
              {scenarioCount > 0 && ` | Scenarios (${scenarioCount})`}
            </h5>
          </div>
          <div className="card-body">
            {hasScenarioLayout && (
              <div className="mb-3">
                <div
                  className="mb-3"
                  role="radiogroup"
                  aria-label="Scenario selector"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                    gap: '0.75rem',
                  }}
                >
                  {scenarioSections.filter((s) => s.scenarioNumber).map((section) => (
                    <button
                      key={section.key}
                      type="button"
                      role="radio"
                      aria-checked={selectedScenarioKey === section.key}
                      className={`btn btn-sm text-start d-flex align-items-center justify-content-between gap-2 ${selectedScenarioKey === section.key ? 'btn-primary' : 'btn-outline-secondary'}`}
                      style={{ padding: '0.5rem 0.75rem' }}
                      onClick={() => {
                        setSelectedScenarioKey(section.key);
                        setShowAllScenarios(false);
                      }}
                    >
                      <span className="text-truncate">{section.label}</span>
                      <span className="badge bg-light text-dark flex-shrink-0">{section.steps.length}</span>
                    </button>
                  ))}
                </div>

                <div className="form-check form-switch mb-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="showAllScenarios"
                    checked={showAllScenarios}
                    onChange={(e) => setShowAllScenarios(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="showAllScenarios">
                    Show all scenarios
                  </label>
                </div>
              </div>
            )}

            {visibleSections.map((section) => (
              <div key={section.key} className="mb-3">
                <div className="alert alert-secondary py-2 mb-3" role="note">
                  <strong>{section.label}</strong>
                </div>

                {section.steps.map(({ step, index }) => (
                  <div key={index} className="card mb-3">
                    <div className="card-body">
                      <div className="row mb-2">
                        <div className="col-md-2">
                          <label className="kone-label">Step #</label>
                          <input
                            type="number"
                            className="kone-input"
                            value={step.step_number || index + 1}
                            readOnly
                            style={{ textAlign: 'center', fontWeight: '600' }}
                          />
                        </div>
                        <div className="col-md-10">
                          <label className="kone-label">Description</label>
                          <textarea
                            className="kone-input"
                            rows="3"
                            value={step.description || ''}
                            readOnly
                            style={{ textAlign: 'left', resize: 'vertical', fontSize: '0.875rem', borderRadius: '6px' }}
                          />
                        </div>
                      </div>

                      {/* View Image Button */}
                      {step.image_s3_key && (
                        <div className="row mt-3">
                          <div className="col-md-12">
                            <button
                              className="btn btn-outline-primary btn-sm"
                              disabled={!imageUrls[step.step_number || String(index + 1)]}
                              onClick={() => window.open(imageUrls[step.step_number || String(index + 1)], '_blank', 'noopener,noreferrer')}
                            >
                              <i className="bi bi-image me-1"></i>
                              {imageUrls[step.step_number || String(index + 1)] ? 'View Image' : 'Loading image...'}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ))}

          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="d-flex justify-content-end gap-2 mb-4">
        <button 
          type="button" 
          className="kone-btn kone-btn-secondary" 
          onClick={() => navigate('/runbook_list')}
        >
          Back to List
        </button>
      </div>
    </div>
  );
}

export default ViewRunbook;
